import { memo, useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { Marker } from '@react-google-maps/api';
import { useTheme } from 'hoi-poi-ui-next';
import { default as GoogleMap } from 'components/Map';
import MapMarkerWithTooltip from 'components/Map/MapMarkerWithTooltip';
import MapMarker from 'components/Map/MapMarker';
import InfoMapRoute from 'containers/components/InfoMapRoute';

const Map = ({ mapPoints }) => {
    const componentWillUnmount = useRef(false);
    const isFirstLoad = useRef(true);
    const hasInitializedRoute = useRef(false);
    const mapComponentRef = useRef(null);
    const markersRef = useRef([]);
    const circlesRef = useRef([]);
    const intervalRef = useRef();
    const [markers, setMarkers] = useState([]);
    const [circles, setCircles] = useState([]);
    const [route, setRoute] = useState();
    const theme = useTheme();

    useEffect(
        () => () => {
            componentWillUnmount.current = true;
        },
        [],
    );

    const onLoad = useCallback(
        (map) => {
            if (map && isFirstLoad.current) {
                const bounds = new window.google.maps.LatLngBounds();
                const newMarkers = [];
                const newCircles = [];
                const newWaypoints = [];

                mapPoints.forEach((point, index) => {
                    const { key, title, icon, coordinates } = point;
                    if (!coordinates) return;
                    const { lat, lng } = coordinates;
                    const newLatLng = new window.google.maps.LatLng(lat, lng);
                    const markerProps = {
                        key,
                        icon,
                        title,
                        position: {
                            lat,
                            lng,
                        },
                        onLoad: (marker) => {
                            markersRef.current[index] = marker;
                        },
                    };
                    const circleProps = {
                        key: `circle-${index}`,
                        icon: {
                            path: 'M-5,0a5,5 0 1,0 10,0a5,5 0 1,0 -10,0',
                            fillColor: theme.colors.blue500,
                            fillOpacity: 1,
                            scale: 1,
                            strokeWeight: 0,
                            anchor: new window.google.maps.Point(0, 0),
                        },
                        position: {
                            lat,
                            lng,
                        },
                        visible: false,
                        onLoad: (marker) => {
                            circlesRef.current[index] = marker;
                        },
                    };

                    const CustomMarker = title ? MapMarkerWithTooltip : MapMarker;

                    newMarkers.push(<CustomMarker {...markerProps} />);
                    newCircles.push(<Marker {...circleProps} />);
                    newWaypoints.push({
                        location: { lat, lng },
                        stopover: true,
                    });
                    bounds.extend(newLatLng);
                });

                map.setCenter(bounds.getCenter());
                map.fitBounds(bounds);
                setMarkers(newMarkers);
                setCircles(newCircles);
                isFirstLoad.current = false;

                if (newWaypoints.length > 1) {
                    const directionsService = new window.google.maps.DirectionsService();
                    const directionsRenderer = new google.maps.DirectionsRenderer({
                        suppressMarkers: true,
                    });
                    directionsRenderer.setMap(map);

                    const origin = newWaypoints[0];
                    const destination = newWaypoints[newWaypoints.length - 1];

                    if (!origin) return;

                    directionsService.route(
                        {
                            origin: { lat: origin.location.lat, lng: origin.location.lng },
                            destination: {
                                lat: destination?.location?.lat,
                                lng: destination?.location?.lng,
                            },
                            waypoints: newWaypoints,
                            travelMode: window.google.maps.TravelMode.DRIVING,
                        },
                        (result, status) => {
                            if (status === window.google.maps.DirectionsStatus.OK) {
                                setRoute(result);
                                directionsRenderer.setDirections(result);
                            } else {
                                console.error(`Error fetching directions ${result}`);
                            }
                        },
                    );
                } else {
                    setRoute();
                }
            }
        },
        [mapPoints, theme],
    );

    const onUnmount = useCallback(() => {
        clearInterval(intervalRef.current);
    }, []);

    const mapProps = useMemo(
        () => ({
            ref: mapComponentRef,
            center: {
                lat: mapPoints[0]?.coordinates?.lat,
                lng: mapPoints[0]?.coordinates?.lng,
            },
            zoom: 11,
            options: {
                zoomControl: true,
                mapTypeControl: true,
                streetViewControl: true,
                scaleControl: false,
                rotateControl: false,
                fullscreenControl: true,
            },
            onLoadMap: onLoad,
            onUnmount,
            onIdle: () => {
                if (!hasInitializedRoute.current) {
                    hasInitializedRoute.current = true;
                }
            },
        }),
        [mapPoints, onLoad, onUnmount],
    );

    return (
        <GoogleMap {...mapProps}>
            {markers}
            {circles}
            <InfoMapRoute googleMapRoute={route} />
        </GoogleMap>
    );
};

export default memo(Map);
